import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NAV_LINKS, type NavLink } from '../../data/navigation';

const isPreLaunch = import.meta.env.VITE_PRE_LAUNCH === 'true';

export function Header(): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header
      data-testid="landing-header"
      className="sticky top-0 z-50 bg-[var(--landing-surface)] border-b border-[var(--landing-border)]"
    >
      <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="text-xl font-bold text-[var(--landing-accent-text)]">
              Resida
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav
            className="hidden md:flex items-center gap-6"
            aria-label="Main navigation"
            ref={dropdownRef}
          >
            {NAV_LINKS.map((link) => (
              <DesktopNavItem
                key={link.label}
                link={link}
                isOpen={openDropdown === link.label}
                onToggle={() =>
                  setOpenDropdown(openDropdown === link.label ? null : link.label)
                }
                onClose={() => setOpenDropdown(null)}
              />
            ))}
          </nav>

          {/* Desktop Auth Links */}
          {!isPreLaunch && (
            <div className="hidden md:flex items-center gap-3">
              <a
                href="/tenant/login"
                data-testid="header-tenant-login"
                className="text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)] transition-colors text-sm font-medium"
              >
                Tenant Login
              </a>
              <a
                href="/landlord/login"
                data-testid="header-landlord-login"
                className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-[var(--landing-radius)] bg-[var(--landing-accent)] hover:bg-[var(--landing-accent-hover)] text-white text-sm font-semibold transition-colors"
              >
                Landlord Login
              </a>
            </div>
          )}

          {/* Mobile Hamburger */}
          <button
            type="button"
            data-testid="header-mobile-menu-toggle"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-md text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] hover:bg-[var(--landing-border)] transition-colors"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          >
            {mobileMenuOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" clipRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" clipRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          id="mobile-menu"
          data-testid="header-mobile-menu"
          className="md:hidden border-t border-[var(--landing-border)] bg-[var(--landing-surface)]"
        >
          <nav className="px-4 py-3 flex flex-col gap-1" aria-label="Mobile navigation">
            {NAV_LINKS.map((link) =>
              link.featureGroups ? (
                <div key={link.label}>
                  <button
                    type="button"
                    className="w-full text-left text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] py-2 text-sm font-medium transition-colors flex items-center justify-between"
                    onClick={() =>
                      setMobileAccordion(mobileAccordion === link.label ? null : link.label)
                    }
                    aria-expanded={mobileAccordion === link.label}
                  >
                    {link.label}
                    <span className="text-xs text-[var(--landing-text-muted)]" aria-hidden="true">
                      {mobileAccordion === link.label ? '−' : '+'}
                    </span>
                  </button>
                  {mobileAccordion === link.label && (
                    <div className="pl-4 pb-2 space-y-3">
                      {link.featureGroups.map((group) => (
                        <div key={group.group}>
                          <div className="text-xs font-medium uppercase tracking-widest text-[var(--landing-text-muted)] mb-1">
                            {group.group}
                          </div>
                          {group.items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              className="block py-1.5 text-sm text-[var(--landing-text-secondary)] hover:text-[var(--landing-accent-text)] transition-colors"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                      <Link
                        to="/features"
                        className="block py-1.5 text-sm text-[var(--landing-accent-text)] font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        See all features &rarr;
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] py-2 text-sm font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ),
            )}
            {!isPreLaunch && (
              <div className="pt-3 mt-2 border-t border-[var(--landing-border)] flex flex-col gap-2">
                <a
                  href="/tenant/login"
                  data-testid="header-tenant-login-mobile"
                  className="text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)] py-2 text-sm font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Tenant Login
                </a>
                <a
                  href="/landlord/login"
                  data-testid="header-landlord-login-mobile"
                  className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-[var(--landing-radius)] bg-[var(--landing-accent)] hover:bg-[var(--landing-accent-hover)] text-white text-sm font-semibold transition-colors text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Landlord Login
                </a>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function DesktopNavItem({
  link,
  isOpen,
  onToggle,
  onClose,
}: {
  link: NavLink;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}): JSX.Element {
  if (!link.featureGroups) {
    return (
      <Link
        to={link.href}
        className="text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] transition-colors text-sm font-medium"
      >
        {link.label}
      </Link>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        className="text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] transition-colors text-sm font-medium inline-flex items-center gap-1"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {link.label}
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[560px] bg-[var(--landing-surface)] border border-[var(--landing-border)] rounded-[var(--landing-radius)] shadow-lg overflow-hidden">
          <div className="grid grid-cols-2 gap-0">
            {link.featureGroups.map((group) => (
              <div key={group.group} className="p-4">
                <div className="text-xs font-medium uppercase tracking-widest text-[var(--landing-text-muted)] mb-3">
                  {group.group}
                </div>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block p-2 rounded-md hover:bg-[var(--landing-bg)] transition-colors group"
                      onClick={onClose}
                    >
                      <div className="text-sm font-medium text-[var(--landing-text-primary)] group-hover:text-[var(--landing-accent-text)] transition-colors">
                        {item.label}
                      </div>
                      <div className="text-xs text-[var(--landing-text-muted)] mt-0.5">
                        {item.description}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--landing-border)] px-4 py-3">
            <Link
              to="/features"
              className="text-sm text-[var(--landing-accent-text)] font-medium hover:text-[var(--landing-accent-text-hover)] transition-colors"
              onClick={onClose}
            >
              See all features &rarr;
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
